// src/pages/tour-types/ViewTourTypes.tsx
import { useMemo, useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import Input from "@/components/shared/ui/Input";

import TourTypesTable, {
  type TourTypeTableFilters,
  type TourTypeRow,
} from "@/components/tour-types/TourTypesTable";
import CreateTourType, {
  type UpsertTourType,
} from "@/components/tour-types/forms/CreateTourType";
import UpdateTourType from "@/components/tour-types/forms/UpdateTourType";

import {
  useTourTypesList,
  useCreateTourTypeMutation,
  useUpdateTourTypeMutation,
  useDeleteTourTypeMutation,
} from "@/api/features/tour-types";
import type { TourType as DtoTourType } from "@/api/features/tour-types";

/* ------------------------------ Component --------------------------- */
export default function ViewTourTypes() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<null | DtoTourType>(null);

  // Confirm dialog (delete)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("Please confirm");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmCta, setConfirmCta] = useState("Confirm");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  // Errors for mutations
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Filters + global search
  const [filters, setFilters] = useState<TourTypeTableFilters>({});
  const [globalQ, setGlobalQ] = useState("");

  // Pagination (server-driven)
  const [page, setPage] = useState(1);
  const DEFAULT_PAGE_SIZE = 10;

  // Reset page when filters or global search change
  useEffect(() => setPage(1), [filters, globalQ]);

  // Build API query (name/description search)
  const q = useMemo(() => {
    const bits = [globalQ, filters.name].filter(Boolean);
    return bits.length ? bits.join(" ").trim() : undefined;
  }, [globalQ, filters.name]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useTourTypesList({
    page,
    limit: DEFAULT_PAGE_SIZE,
    q,
    // includeDeleted: false by default (backend handles soft-delete)
  });

  // Use server-returned pagination if present
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_PAGE_SIZE;
  const currentPage = data?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Map API items → table rows (server already paginates)
  const pageRows: TourTypeRow[] = (data?.items ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    description: t.description ?? "",
  }));

  /* ---------------------------- Mutations ---------------------------- */
  const createMut = useCreateTourTypeMutation();
  const updateMut = useUpdateTourTypeMutation(editOpen?.id ?? 0);
  const deleteMut = useDeleteTourTypeMutation();

  const onCreate = async (payload: UpsertTourType) => {
    setCreateError(null);
    try {
      await createMut.mutateAsync({
        name: payload.name.trim(),
        description: payload.description?.trim() || undefined,
      });
      setCreateOpen(false);
      refetch();
    } catch (e: any) {
      setCreateError(e?.message || "Failed to create tour type");
    }
  };

  const onEdit = async (payload: { name: string; description?: string }) => {
    if (!editOpen) return;
    setUpdateError(null);
    try {
      await updateMut.mutateAsync({
        name: payload.name.trim(),
        description: payload.description?.trim() || undefined,
      });
      setEditOpen(null);
      refetch();
    } catch (e: any) {
      setUpdateError(e?.message || "Failed to update tour type");
    }
  };

  const askDelete = (tt: TourTypeRow) => {
    setDeleteError(null);
    setConfirmTitle("Delete tour type");
    setConfirmMsg(
      `Do you want to delete the tour type “${tt.name}”? This action cannot be undone.`
    );
    setConfirmCta("Delete");
    setConfirmAction(() => async () => {
      try {
        await deleteMut.mutateAsync(Number(tt.id));
        setConfirmOpen(false);
        refetch();
      } catch (e: any) {
        setDeleteError(e?.message || "Failed to delete tour type");
      }
    });
    setConfirmOpen(true);
  };

  const serialStart = (currentPage - 1) * pageSize;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Tour Types</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Input
              value={globalQ}
              onChange={(e) => setGlobalQ(e.target.value)}
              placeholder="Search tour types…"
              className="w-56 pl-8"
            />
          </div>

          <button
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
            onClick={() => setCreateOpen(true)}
          >
            <FiPlus /> Add Tour Type
          </button>
        </div>
      </div>

      {/* Error banners */}
      {isError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load tour types: {(error as Error)?.message || "Unknown error"}
        </div>
      )}
      {createError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {createError}
        </div>
      )}
      {updateError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {updateError}
        </div>
      )}
      {deleteError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {deleteError}
        </div>
      )}

      {/* Table */}
      <TourTypesTable
        rows={pageRows}
        pageRows={pageRows}
        serialStart={serialStart}
        filters={filters}
        setFilters={setFilters}
        onEdit={(r) => {
          const full = (data?.items ?? []).find((x) => x.id === r.id) as
            | DtoTourType
            | undefined;
          if (full) setEditOpen(full);
        }}
        onDelete={askDelete}
      />

      {/* Pagination */}
      <div className="mt-0 flex items-center justify-between gap-2 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">
          {isLoading ? (
            <span>Loading…</span>
          ) : (
            <>
              Showing{" "}
              <span className="font-medium text-gray-700">
                {total === 0 ? 0 : serialStart + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-gray-700">
                {Math.min(serialStart + pageSize, total)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-700">{total}</span> results
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1 || isLoading}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Prev
          </button>
          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === currentPage;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`rounded-md px-2.5 py-1.5 text-sm ${
                    active
                      ? "bg-sky-600 text-white"
                      : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && (
              <span className="px-1 text-sm text-gray-500">…</span>
            )}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || isLoading}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Modals */}
      <Modal
        title="Create Tour Type"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <CreateTourType
          onSubmit={onCreate}
          onCancel={() => setCreateOpen(false)}
        />
      </Modal>

      <Modal
        title="Edit Tour Type"
        open={!!editOpen}
        onClose={() => setEditOpen(null)}
      >
        {editOpen ? (
          <UpdateTourType
            tourType={{
              id: editOpen.id,
              name: editOpen.name,
              description: editOpen.description ?? "",
            }}
            onSubmit={(updated) =>
              onEdit({
                name: updated.name,
                description: updated.description,
              })
            }
            onCancel={() => setEditOpen(null)}
          />
        ) : null}
      </Modal>

      {/* Confirm delete */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMsg}
        confirmText={confirmCta}
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </AdminLayout>
  );
}
